/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Enhanced App Registry — the SINGLE SOURCE OF TRUTH for all modules and
 * their internal tabs. Every new module added here automatically appears in:
 *   - User Create/Edit form (tab-level permission checkboxes)
 *   - Module tab bars (filtered by user.tabAccess)
 *   - Route-level security gates
 *
 * To add a new module: add one entry below. Zero other files need changing
 * for the permission system to pick it up.
 */

import { UserRole } from '../domain/entities';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ModuleTabDefinition {
  /** Unique id within the module (e.g. 'database', 'inbox'). */
  id: string;
  /** Human-readable label shown in the tab bar and the permission form. */
  name: string;
  /**
   * Optional view variants for granular in-tab gating.
   * When defined, each variant produces a separate permission key:
   *   `moduleId:tabId:variant` (e.g. `aggregator:dashboard:admin`).
   * Admin users and platform staff bypass variant checks automatically.
   * A user needs at least ONE variant key to see the tab at all.
   */
  variants?: string[];
}

export interface ApiEndpointDefinition {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  desc: string;
  /** Scope suffix — combined with module id to form full scope (e.g. 'rider-ops' + 'read' → 'rider-ops:read') */
  scopeType: 'read' | 'write';
  /** Sample POST/PUT body for explorer docs */
  sampleBody?: Record<string, unknown>;
}

export interface WebhookEventDefinition {
  event: string;
  label: string;
  desc: string;
  /** Sample payload shape for docs */
  samplePayload?: Record<string, unknown>;
}

export interface MetricDefinition {
  /** Unique metric id used as key in PlanLimit.usageLimits (e.g. 'max_riders'). */
  id: string;
  /** Human-readable label shown in admin UI (e.g. 'Max Active Riders'). */
  label: string;
}

/**
 * Defines the data footprint for backup/restore/wipe operations.
 * Each module declares which D1 tables and R2 folders it owns.
 */
export interface DataFootprintDefinition {
  /** D1 tables owned by this module (order matters for DELETE — children first). */
  tables: string[];
  /** R2 folder prefixes (relative to orgId root) holding module assets. */
  assetFolders: string[];
}

export interface PlatformModuleDefinition {
  /** Unique module id — matches MODULE_IDS / AppId values. */
  id: string;
  /** Human-readable module name. */
  name: string;
  /** Short display name for compact UI (sidebar). Falls back to `name`. */
  shortName?: string;
  /** Ordered list of internal tabs/routes the module exposes. */
  tabs: ModuleTabDefinition[];
  /** API endpoints this module exposes through the gateway. */
  apiEndpoints?: ApiEndpointDefinition[];
  /** Webhook events this module can fire. */
  webhookEvents?: WebhookEventDefinition[];
  /** Roles that are allowed to access this module.
   *  undefined = all roles (no restriction). */
  supportedRoles?: UserRole[];
  /** Numeric usage metrics this module wants to limit in subscription plans. */
  metrics?: MetricDefinition[];
  /** Data footprint — tables and asset folders this module owns (for backup/restore/wipe). */
  dataFootprint?: DataFootprintDefinition;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

export const PLATFORM_MODULES: PlatformModuleDefinition[] = [
  {
    id: 'rider-manager',
    name: 'Rider Operations',
    shortName: 'Riders',
    supportedRoles: [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE],
    tabs: [
      { id: 'dashboard', name: 'Dashboard' },
      { id: 'database', name: 'Rider Database',     variants: ['admin', 'manager'] },
      { id: 'inbox',    name: 'Request Inbox',      variants: ['admin'] },
      { id: 'submit',   name: 'New Request' },
      { id: 'transfers',name: 'Transfer Requests',  variants: ['admin', 'manager'] },
      { id: 'analytics',name: 'Analytics',           variants: ['admin', 'manager'] },
      { id: 'settings', name: 'App Settings',        variants: ['admin'] },
    ],
    apiEndpoints: [
      { method: 'GET',  path: '/api/riders',                  desc: 'Fetch all riders',              scopeType: 'read' },
      { method: 'POST', path: '/api/riders',                  desc: 'Create rider request',           scopeType: 'write', sampleBody: { name: 'Jane Doe', mobile: '0123456789', branchId: 'branch-kfc-airport' } },
      { method: 'GET',  path: '/api/riders/{RIDER_ID}/history', desc: 'Rider career timeline',        scopeType: 'read' },
      { method: 'GET',  path: '/api/branches',                 desc: 'Brand branch registry',          scopeType: 'read' },
    ],
    webhookEvents: [
      { event: 'rider.approved', label: 'On Approval', desc: 'Fires when a rider request is approved', samplePayload: { event: 'rider.approved', riderId: 'abc', name: 'Jane Doe' } },
      { event: 'rider.status_change', label: 'On Status Change', desc: 'Fires when rider status changes', samplePayload: { event: 'rider.status_change', riderId: 'abc', from: 'ACTIVE', to: 'INACTIVE' } },
    ],
    metrics: [
      { id: 'max_riders', label: 'Max Active Riders' },
      { id: 'max_branches', label: 'Max Branches' },
    ],
    dataFootprint: {
      tables: ['rider_history', 'workflow_requests', 'riders', 'rider_settings'],
      assetFolders: ['riders/photos'],
    },
  },
  {
    id: 'aggregator',
    name: 'Aggregator Operations',
    shortName: 'Aggregators',
    supportedRoles: [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER],
    tabs: [
      { id: 'dashboard',   name: 'Dashboard',            variants: ['admin', 'manager'] },
      { id: 'management',  name: 'Campaign Management',  variants: ['admin'] },
      { id: 'submit',      name: 'Submit Items' },
      { id: 'review',      name: 'Submission Reviews',   variants: ['admin', 'manager'] },
      { id: 'deployment',  name: 'Launch Readiness',     variants: ['admin', 'manager'] },
      { id: 'live',        name: 'Live Campaigns',       variants: ['admin', 'manager'] },
      { id: 'analytics',   name: 'Analytics Dashboard',  variants: ['admin', 'manager'] },
      { id: 'archive',     name: 'Campaign Archive',     variants: ['admin', 'manager'] },
      { id: 'settings',    name: 'Aggregator Settings',  variants: ['admin'] },
    ],
    apiEndpoints: [
      { method: 'GET',  path: '/api/aggregator/campaigns',    desc: 'Fetch live campaigns',           scopeType: 'read' },
      { method: 'POST', path: '/api/aggregator/submit',       desc: 'Submit campaign items',          scopeType: 'write', sampleBody: { campaignId: 'camp_01', items: [{ name: 'Burger', price: 5.99 }] } },
      { method: 'GET',  path: '/api/aggregator/submissions',  desc: 'Fetch all submissions',          scopeType: 'read' },
    ],
    webhookEvents: [
      { event: 'aggregator.launch', label: 'On Launch', desc: 'Fires when a campaign goes live', samplePayload: { event: 'aggregator.launch', campaignId: 'camp_01', name: 'Summer Promo' } },
      { event: 'aggregator.status_change', label: 'On Status Change', desc: 'Fires on campaign status change', samplePayload: { event: 'aggregator.status_change', campaignId: 'camp_01', from: 'DRAFT', to: 'APPROVED' } },
    ],
    metrics: [
      { id: 'max_campaigns', label: 'Max Active Campaigns' },
      { id: 'max_submissions', label: 'Max Submissions' },
    ],
    dataFootprint: {
      tables: [
        'submission_discussion', 'submission_review_history', 'submission_items',
        'campaign_submissions', 'campaign_brand_targets', 'campaign_settings', 'campaigns',
      ],
      assetFolders: ['aggregator/assets'],
    },
  },
  {
    id: 'menu-manage',
    name: 'Menu Management',
    shortName: 'Menus',
    supportedRoles: [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE],
    tabs: [
      { id: 'dashboard',  name: 'Dashboard',      variants: ['admin', 'manager'] },
      { id: 'profiles',   name: 'Menu Profiles',  variants: ['admin', 'manager'] },
      { id: 'library',    name: 'Option Library',  variants: ['admin', 'manager'] },
      { id: 'pantry',     name: 'Ingredient Registry', variants: ['admin'] },
      { id: 'ministry',   name: 'Ministry Vault', variants: ['admin', 'manager'] },
      { id: 'preview',    name: 'Digital Preview', variants: ['admin', 'manager'] },
      { id: 'requests',   name: 'Update Requests', variants: ['admin', 'manager'] },
      { id: 'settings',   name: 'Settings',       variants: ['admin'] },
    ],
    apiEndpoints: [
      { method: 'GET',  path: '/api/menu/profiles',           desc: 'Fetch menu profiles',            scopeType: 'read' },
      { method: 'GET',  path: '/api/menu/items',              desc: 'Fetch all menu items',           scopeType: 'read' },
      { method: 'POST', path: '/api/menu/profiles',           desc: 'Create menu profile',            scopeType: 'write', sampleBody: { name: 'Summer Menu', brandId: 'brand_01' } },
    ],
    webhookEvents: [
      { event: 'menu.published', label: 'On Publish', desc: 'Fires when a menu profile is published', samplePayload: { event: 'menu.published', profileId: 'prof_01', name: 'Summer Menu' } },
    ],
    metrics: [
      { id: 'max_menu_profiles', label: 'Max Menu Profiles' },
    ],
    dataFootprint: {
      tables: [
        'menu_item_options', 'menu_option_groups', 'menu_items', 'menu_categories',
        'menu_profile_ministry_files', 'ministry_files', 'library_modifier_options',
        'library_modifiers', 'menu_settings', 'menu_profiles',
      ],
      assetFolders: ['menu/images', 'menu/ministry'],
    },
  },
  {
    id: 'support-desk',
    name: 'Support & Ticketing',
    shortName: 'Support',
    supportedRoles: [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE],
    tabs: [
      { id: 'dashboard',              name: 'Dashboard',              variants: ['admin', 'manager'] },
      { id: 'ticket-center',          name: 'Ticket Center',          variants: ['admin', 'manager'] },
      { id: 'track-lifecycle',        name: 'Track Lifecycle',        variants: ['admin', 'manager'] },
      { id: 'operational-insights',   name: 'Operational Insights',   variants: ['admin', 'manager'] },
      { id: 'knowledge-base',         name: 'Knowledge Base',         variants: ['admin', 'manager'] },
      { id: 'settings',               name: 'Settings',               variants: ['admin'] },
    ],
    apiEndpoints: [
      { method: 'GET',  path: '/api/support/tickets', desc: 'Fetch tickets',       scopeType: 'read' },
      { method: 'POST', path: '/api/support/tickets', desc: 'Create a ticket',     scopeType: 'write', sampleBody: { subject: 'Printer offline', category: 'OPERATIONAL', priority: 'HIGH', description: 'KOT printer is down at ABD-03.' } },
    ],
    webhookEvents: [
      { event: 'support:new_ticket', label: 'On New Ticket', desc: 'Fires when a manager creates a new support ticket', samplePayload: { event: 'support:new_ticket', ticketId: 'TKT-1001', subject: 'Printer offline', priority: 'HIGH' } },
      { event: 'support:ticket_resolved', label: 'On Resolve', desc: 'Fires when a ticket is resolved', samplePayload: { event: 'support:ticket_resolved', ticketId: 'TKT-1001', resolvedBy: 'admin1' } },
    ],
    metrics: [
      { id: 'max_tickets', label: 'Max Open Tickets' },
      { id: 'max_kb_articles', label: 'Max Knowledge Base Articles' },
    ],
    dataFootprint: {
      tables: [
        'ticket_messages', 'ticket_resolution_history', 'ticket_status_history',
        'ticket_sla', 'issue_report_attachments', 'issue_report_aggregators',
        'issue_report_brands', 'ticket_issue_reports', 'tickets',
        'support_settings', 'knowledge_articles',
      ],
      assetFolders: ['support/attachments'],
    },
  },
  {
    id: 'system-tools',
    name: 'System Administration',
    shortName: 'System',
    tabs: [
      { id: 'logs',         name: 'System Logs' },
      { id: 'users',        name: 'User Accounts' },
      { id: 'api',          name: 'API Management' },
      { id: 'brands',       name: 'Brand Registry' },
      { id: 'departments',  name: 'Department Registry' },
    ],
  },
  // ── Test App (architecture verification) ────────────────────────────────────
  {
    id: 'market-map',
    name: 'Market Intelligence',
    shortName: 'Map',
    supportedRoles: [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE],
    tabs: [
      { id: 'dashboard',         name: 'Dashboard',            variants: ['admin', 'manager'] },
      { id: 'live-map',          name: 'Live Map',              variants: ['admin', 'manager'] },
      { id: 'location-manager',  name: 'Location Manager',      variants: ['admin', 'manager'] },
      { id: 'target-management', name: 'Target Management',     variants: ['admin'] },
      { id: 'sales-history',     name: 'Sales History',          variants: ['admin'] },
      { id: 'analytics',          name: 'Analytics',             variants: ['admin', 'manager'] },
      { id: 'archive',            name: 'Intelligence Archive',   variants: ['admin'] },
      { id: 'settings',           name: 'Settings',              variants: ['admin'] },
    ],
    apiEndpoints: [
      { method: 'GET',  path: '/api/map/locations',  desc: 'Fetch map locations',  scopeType: 'read' },
      { method: 'POST', path: '/api/map/locations',  desc: 'Create a location',    scopeType: 'write', sampleBody: { name: 'Al Wakra Branch', type: 'INTERNAL', lat: 25.17, lon: 51.61, dailyTarget: 5000 } },
      { method: 'POST', path: '/api/map/sales',      desc: 'Submit daily sales',   scopeType: 'write', sampleBody: { locationId: 'loc_01', salesAmount: 4200 } },
    ],
    webhookEvents: [
      { event: 'map:sales_entered', label: 'On Sales Entry', desc: 'Fires when a manager submits daily sales for a branch', samplePayload: { event: 'map:sales_entered', locationId: 'loc_01', salesAmount: 4200 } },
    ],
    metrics: [
      { id: 'max_map_locations', label: 'Max Map Locations' },
    ],
    dataFootprint: {
      tables: ['map_locations', 'map_sales_entries', 'map_settings'],
      assetFolders: [],
    },
  },
  // ── Test App (architecture verification) ────────────────────────────────────
  {
    id: 'test-app',
    name: 'Test Application',
    shortName: 'Test',
    tabs: [
      { id: 'dashboard', name: 'Dashboard' },
    ],
    metrics: [
      { id: 'max_test_items', label: 'Max Test Items' },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build a fully-qualified tab permission key.
 * Without variant: `moduleId:tabId` (e.g. `rider-manager:submit`).
 * With variant:    `moduleId:tabId:variant` (e.g. `rider-manager:database:admin`).
 */
export function tabPermissionKey(moduleId: string, tabId: string, variant?: string): string {
  return variant ? `${moduleId}:${tabId}:${variant}` : `${moduleId}:${tabId}`;
}

/**
 * Return all tab permission keys for a given module.
 * Tabs with variants emit one key per variant instead of a base key.
 */
export function allTabKeysForModule(moduleId: string): string[] {
  const mod = PLATFORM_MODULES.find(m => m.id === moduleId);
  if (!mod) return [];
  return mod.tabs.flatMap(t => {
    if (t.variants && t.variants.length > 0) {
      return t.variants.map(v => tabPermissionKey(moduleId, t.id, v));
    }
    return [tabPermissionKey(moduleId, t.id)];
  });
}

/**
 * Return all tab permission keys across all modules.
 * Variant tabs emit one key per variant.
 */
export function allTabKeys(): string[] {
  return PLATFORM_MODULES.flatMap(m =>
    m.tabs.flatMap(t => {
      if (t.variants && t.variants.length > 0) {
        return t.variants.map(v => tabPermissionKey(m.id, t.id, v));
      }
      return [tabPermissionKey(m.id, t.id)];
    })
  );
}

// ─── API Helpers ──────────────────────────────────────────────────────────────

/** Modules that actually expose API endpoints. */
export function getApiModules(): PlatformModuleDefinition[] {
  return PLATFORM_MODULES.filter(m => m.apiEndpoints && m.apiEndpoints.length > 0);
}

/** Build a scope string like 'rider-manager:read'. */
export function buildApiScope(moduleId: string, scopeType: 'read' | 'write'): string {
  return `${moduleId}:${scopeType}`;
}

/**
 * All unique API scopes derivable from the registry.
 * Returns e.g. ['rider-manager:read', 'rider-manager:write', 'aggregator:read', ...]
 */
export function allApiScopes(): string[] {
  const scopes = new Set<string>();
  for (const mod of getApiModules()) {
    for (const ep of mod.apiEndpoints!) {
      scopes.add(buildApiScope(mod.id, ep.scopeType));
    }
  }
  return [...scopes];
}

/**
 * All webhook events from the registry grouped by module.
 */
export function allWebhookEvents(): { moduleId: string; moduleName: string; events: WebhookEventDefinition[] }[] {
  return PLATFORM_MODULES
    .filter(m => m.webhookEvents && m.webhookEvents.length > 0)
    .map(m => ({ moduleId: m.id, moduleName: m.name, events: m.webhookEvents! }));
}

// ─── Metric Discovery ─────────────────────────────────────────────────────────

/** Platform-level metrics not tied to any single module. */
export const PLATFORM_METRICS: MetricDefinition[] = [
  { id: 'max_users', label: 'Max Users' },
];

/** Collect all metrics across platform + modules for dynamic UI rendering. */
export function getAllMetrics(): { moduleId: string | null; moduleName: string; metrics: MetricDefinition[] }[] {
  const result: { moduleId: string | null; moduleName: string; metrics: MetricDefinition[] }[] = [
    { moduleId: null, moduleName: 'Platform', metrics: PLATFORM_METRICS },
  ];
  for (const mod of PLATFORM_MODULES) {
    if (mod.metrics && mod.metrics.length > 0) {
      result.push({ moduleId: mod.id, moduleName: mod.name, metrics: mod.metrics });
    }
  }
  return result;
}
